// ── FsrsSettings — окно «Настройки повторения» (FSRS-4.5) ────────────────────
// Удержание, начало дня, дневные лимиты, шаги заучивания, максимальный интервал,
// порядок новых. Значения хранятся в localStorage и синхронизируются (markSetting).
// Собрано из деталей src/ui: Modal, Field, Button.

import { useState } from 'react';
import { getFsrsSettings, FSRS_DEFAULTS, intervalForRetention, getNewMix, NEW_MIX_OPTIONS, parseSteps, formatSteps } from '../srs';
import { markSetting } from '../settings';
import { t } from '../i18n';
import Icon from '../Icon';
import { Modal, Field, Button, usePalette } from '../ui';

const KEYS = ['ac_fsrs_retention', 'ac_day_start_hour', 'ac_new_limit', 'ac_review_limit', 'ac_max_interval', 'ac_new_mix', 'ac_learn_steps', 'ac_relearn_steps'];
const int = (v, min, max, dflt) => Math.max(min, Math.min(max, parseInt(v, 10) || dflt));

function Section({ title, children, hint }) {
  const C = usePalette();
  return (
    <section style={{ marginBottom: 20 }}>
      <h3 style={{ margin: '0 0 10px', paddingBottom: 6, borderBottom: `1px solid ${C.border}`, fontSize: 15, fontWeight: 600, color: C.text }}>{title}</h3>
      {children}
      {hint && <div style={{ fontSize: 12, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
    </section>
  );
}

export default function FsrsSettings({ onClose }) {
  const C = usePalette();
  const [s, setS] = useState(() => {
    const f = getFsrsSettings();
    return { ...f, newMix: getNewMix(), learnStr: formatSteps(f.learnSteps), relearnStr: formatSteps(f.relearnSteps) };
  });
  const learnParsed = parseSteps(s.learnStr), relearnParsed = parseSteps(s.relearnStr);
  const learnBad = !learnParsed.length, relearnBad = !!s.relearnStr.trim() && !relearnParsed.length;
  const setVal = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = () => {
    try {
      const put = (k, v) => { localStorage.setItem(k, String(v)); markSetting(k); };
      put('ac_fsrs_retention', s.retention);
      put('ac_day_start_hour', s.dayStart);
      put('ac_new_limit', s.newLimit);
      put('ac_review_limit', s.reviewLimit);
      put('ac_max_interval', s.maxInterval);
      put('ac_new_mix', s.newMix);
      if (!learnBad) put('ac_learn_steps', formatSteps(learnParsed));
      if (!relearnBad) put('ac_relearn_steps', formatSteps(relearnParsed));
      window.dispatchEvent(new CustomEvent('ac:settings-updated', { detail: { keys: KEYS } }));
    } catch { /* нет доступа к хранилищу */ }
    onClose();
  };
  const reset = () => setS({ ...FSRS_DEFAULTS, newMix: 'mix', learnStr: formatSteps(FSRS_DEFAULTS.learnSteps), relearnStr: formatSteps(FSRS_DEFAULTS.relearnSteps) });

  // Примерно во сколько раз чаще (реже) повторения, чем при 0.90.
  const reviewMult = (1 / Math.log(s.retention)) / (1 / Math.log(0.90));
  const reviewHint = reviewMult >= 1
    ? t("≈ {0}× ревью vs дефолт (0.90)", [reviewMult.toFixed(2)])
    : t("≈ {0}× реже vs дефолт (0.90)", [(1 / reviewMult).toFixed(2)]);
  const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0 12px' };

  return (
    <Modal size="sm" onClose={onClose}
      title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon n="brain" s={18} />{t("Настройки повторения")}</span>}
      footer={<>
        <Button variant="ghost" onClick={reset} style={{ marginRight: 'auto' }}>{t("↺ Сбросить")}</Button>
        <Button onClick={onClose}>{t("Отмена")}</Button>
        <Button variant="primary" onClick={save} disabled={learnBad || relearnBad}>{t("Сохранить")}</Button>
      </>}>
      <Section title={t("Желаемое удержание (Retention)")}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '2px 12px', marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{Math.round(s.retention * 100)}%</span>
          <span style={{ fontSize: 12, color: C.faint }}>{reviewHint}</span>
        </div>
        <input type="range" min={0.70} max={0.97} step={0.01} value={s.retention} aria-label={t("Желаемое удержание (Retention)")}
          onChange={(e) => setVal('retention', parseFloat(e.target.value))}
          style={{ width: '100%', height: 32, margin: 0, accentColor: C.blue, cursor: 'pointer' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, color: C.faint }}>
          <span>{t("70% — меньше повторений")}</span>
          <span style={{ textAlign: 'right' }}>{t("97% — крепче память")}</span>
        </div>
        <div style={{ fontSize: 12.5, color: C.mid, lineHeight: 1.5, marginTop: 10, padding: '9px 11px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          {t("Выше значение = лучше память, но")} <b>{t("больше ежедневных повторений")}</b>{t(". Рекомендуем 90% (дефолт FSRS).")}
        </div>
        {/* Двигаете ползунок — числа меняются: сразу видно, что значит удержание на практике. */}
        <div style={{ marginTop: 12, padding: '10px 12px', background: C.bluePale, border: `1px solid ${C.blue}33`, borderRadius: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blueDark, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>{t("Что это значит на практике")}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
            {[[7, t("была через 7д →")], [30, t("была через 30д →")], [180, t("была через 180д →")]].map(([d, l]) => (
              <div key={d} style={{ padding: '5px 6px', background: C.card, borderRadius: 9, textAlign: 'center', minWidth: 0 }}>
                <div style={{ fontSize: 11, color: C.faint, overflowWrap: 'break-word' }}>{l}</div>
                <div style={{ fontSize: 15, color: C.blueDark }}>{intervalForRetention(d, s.retention)}{t("д")}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: C.faint, marginTop: 6, lineHeight: 1.4 }}>
            {t("Stability карты при удачном «Знаю» × (1.0..3.4) — растёт быстрее при низком retention. 90% — компромисс между «помнить надёжно» и «не утонуть в повторениях».")}
          </div>
        </div>
      </Section>

      <Section title={t("Начало нового дня")} hint={t("Карточки со сроком до этого часа считаются сегодняшними. Если занимаетесь поздно ночью — поставьте 4 (как в Anki) или 6.")}>
        <Field aria-label={t("Начало нового дня")} type="number" min={0} max={23} value={s.dayStart} suffix={t("часов утра")} style={{ marginBottom: 0 }}
          onChange={(e) => setVal('dayStart', int(e.target.value, 0, 23, 0))} />
      </Section>

      <Section title={t("Дневные лимиты")} hint={t("Лимит на сессию повторения. 0 = без лимита.")}>
        <div style={grid2}>
          <Field label={t("Новые карты")} type="number" min={0} max={9999} value={s.newLimit} style={{ marginBottom: 0 }}
            onChange={(e) => setVal('newLimit', Math.max(0, parseInt(e.target.value, 10) || 0))} />
          <Field label={t("Повторения")} type="number" min={0} max={9999} value={s.reviewLimit} style={{ marginBottom: 0 }}
            onChange={(e) => setVal('reviewLimit', Math.max(0, parseInt(e.target.value, 10) || 0))} />
        </div>
      </Section>

      <Section title={t("Шаги заучивания")} hint={t("Через сколько карточка вернётся, пока вы её учите: m — минуты, h — часы, d — дни, через пробел. «Хорошо» ведёт на следующий шаг, после последнего карточка уходит в повторение. По умолчанию 1m 10m и 10m, как в Anki.")}>
        <div style={grid2}>
          <Field label={t("Новые карты")} value={s.learnStr} placeholder="1m 10m" aria-invalid={learnBad} inputStyle={learnBad ? { borderColor: C.danger } : undefined} style={{ marginBottom: 0 }}
            onChange={(e) => setVal('learnStr', e.target.value)} />
          <Field label={t("После «Снова»")} value={s.relearnStr} placeholder={t("нет")} aria-invalid={relearnBad} inputStyle={relearnBad ? { borderColor: C.danger } : undefined} style={{ marginBottom: 0 }}
            onChange={(e) => setVal('relearnStr', e.target.value)} />
        </div>
      </Section>

      <Section title={t("Максимальный интервал")} hint={t("Карточка не уйдёт дальше этого срока, даже если вы её хорошо помните. 36500 — без ограничения.")}>
        <Field aria-label={t("Максимальный интервал")} type="number" min={1} max={36500} value={s.maxInterval} suffix={t("дней")} style={{ marginBottom: 0 }}
          onChange={(e) => setVal('maxInterval', int(e.target.value, 1, 36500, 1))} />
      </Section>

      <Section title={t("Порядок показа")} hint={t("Сначала карточки, которые вы сейчас учите, потом повторения — самые просроченные первыми. Новые берутся по порядку проектов (его можно поменять перетаскиванием: панель «Повторение» → «Порядок»), внутри проекта — по месту в аудио.")}>
        <div role="radiogroup" aria-label={t("Порядок показа")} style={{ display: 'flex', flexDirection: 'column' }}>
          {NEW_MIX_OPTIONS.map(([id, label]) => (
            <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40, fontSize: 14, color: C.text, cursor: 'pointer' }}>
              <input type="radio" name="ac-new-mix" checked={s.newMix === id} onChange={() => setVal('newMix', id)} style={{ accentColor: C.blue, width: 18, height: 18, margin: 0, flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>{t("Новые:")} {label.toLowerCase()}</span>
            </label>
          ))}
        </div>
      </Section>
    </Modal>
  );
}
